import React from 'react';
import { Helmet } from 'react-helmet-async';
import { motion } from 'framer-motion';
import { Section, Heading, Badge, Button } from '../components/UI.jsx';
import { team, metadata } from '../data.js';
import { Heart, ShieldCheck, Users, Activity } from 'lucide-react';
import { getOptimizedImageUrl } from '../lib/utils.js';

const About = () => {
  return (
    <div className="bg-[#F1E5DC] selection:bg-[#A8814E] selection:text-white">
      <Helmet>
        <title>Mød Teamet - Professionelle Instruktører | LK STUDIO</title>
        <meta name="description" content="Mød teamet bag LK STUDIO på Frederiksberg. Vi er uddannede fysioterapeuter og EMS-instruktører, der sikrer dig en tryg og effektiv træning." />
        <link rel="canonical" href="https://lkstudioems.com/om-os" />

        {/* Open Graph / Facebook */}
        <meta property="og:type" content="website" />
        <meta property="og:url" content="https://lkstudioems.com/om-os" />
        <meta property="og:title" content="Mød Teamet - Professionelle Instruktører | LK STUDIO" />
        <meta property="og:description" content="Mød teamet bag LK STUDIO på Frederiksberg. Vi er uddannede fysioterapeuter og EMS-instruktører, der sikrer dig en tryg og effektiv træning." />
        <meta property="og:image" content="https://assets.auxodev.dk/assets/766f1303-2002-4537-8cbb-d8bd7f1c5c0f/09230a49-8e0a-4270-b212-ef3872e2dc31.jpg" />
        <meta property="og:site_name" content="LK STUDIO" />

        {/* Twitter */}
        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:url" content="https://lkstudioems.com/om-os" />
        <meta name="twitter:title" content="Mød Teamet - Professionelle Instruktører | LK STUDIO" />
        <meta name="twitter:description" content="Mød teamet bag LK STUDIO på Frederiksberg. Vi er uddannede fysioterapeuter og EMS-instruktører, der sikrer dig en tryg og effektiv træning." />
        <meta name="twitter:image" content="https://assets.auxodev.dk/assets/766f1303-2002-4537-8cbb-d8bd7f1c5c0f/09230a49-8e0a-4270-b212-ef3872e2dc31.jpg" />
      </Helmet>
      {/* Hero Section */}
      <section className="relative min-h-[85vh] overflow-hidden bg-[#F1E5DC] pt-32 lg:pt-44 pb-16 md:pb-24">
        <div className="container relative z-10 mx-auto px-4 sm:px-6 lg:px-10">
          <div className="max-w-5xl">
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 1, ease: [0.22, 1, 0.36, 1] }}
            >
              <Badge className="mb-8">
                Hjertet bag LK STUDIO
              </Badge>
              <h1 className="font-serif text-5xl font-normal leading-[0.9] tracking-[-0.05em] text-[#1C1B1A] sm:text-7xl lg:text-[7.5rem]">
                Mød dit <br />
                <span className="text-[#9A8E81] italic">Team</span>
              </h1>
              
              <div className="mt-12 grid gap-10 border-t border-[#CEBDAA] pt-12 lg:grid-cols-[1.2fr_0.8fr]">
                <div className="max-w-2xl">
                  <p className="text-xl leading-relaxed text-[#494540] sm:text-2xl">
                    Vi er et dedikeret team af specialister, der brænder for at hjælpe dig i bedre form gennem effektiv og sikker træning.
                  </p>
                  <div className="mt-10 flex flex-wrap gap-4">
                    <Button 
                      variant="primary" 
                      onClick={() => window.open('https://booking.lkstudioems.com/book-online', '_blank')}
                    >
                      KONTAKT OS
                    </Button>
                    <Button 
                      variant="outline"
                      onClick={() => {
                        const el = document.getElementById('team');
                        el?.scrollIntoView({ behavior: 'smooth' });
                      }}
                    >
                      SE TEAMET
                    </Button>
                  </div>
                </div>
              </div>
            </motion.div>
          </div>
        </div>

        {/* Subtle background element */}
        <div className="absolute right-0 top-0 -z-10 h-full w-1/2 overflow-hidden opacity-30 lg:opacity-80">
          <div className="relative h-full w-full">
            <img 
              src={getOptimizedImageUrl("https://assets.auxodev.dk/assets/766f1303-2002-4537-8cbb-d8bd7f1c5c0f/6247e0a4-e33c-4137-bc63-9be91c5e9781.jpg", { width: 1200, quality: 80 })} 
              className="w-full h-full object-cover grayscale-[0.4]" 
              alt="Professionel EMS Træning hos LK STUDIO" 
            />
            <div className="absolute inset-0 bg-gradient-to-r from-[#F1E5DC] via-[#F1E5DC]/50 to-transparent" />
          </div>
        </div>
      </section>

      {/* Team Grid */}
      <Section id="team" className="pt-10 overflow-visible">
        <div className="max-w-7xl mx-auto grid md:grid-cols-3 gap-x-12 gap-y-24">
          {team.map((member, i) => (
            <motion.div 
              key={i}
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ delay: i * 0.1, duration: 0.8, ease: "easeOut" }}
              className={`group relative ${i % 2 !== 0 ? 'md:mt-24' : ''}`}
            >
              {/* Decorative Background Shape on Hover */}
              <div className="absolute -inset-6 bg-[#CEBDAA]/10 rounded-[4rem] scale-95 opacity-0 group-hover:opacity-100 group-hover:scale-100 transition-all duration-500 -z-10" />
              
              <div className="relative">
                <div className="aspect-[4/5] rounded-[4rem] overflow-hidden shadow-[0_32px_64px_-16px_rgba(0,0,0,0.1)] mb-10 relative group-hover:shadow-[0_48px_80px_-16px_rgba(0,0,0,0.15)] transition-all duration-500">
                  <motion.img 
                    whileHover={{ scale: 1.05 }}
                    transition={{ duration: 0.8 }}
                    src={getOptimizedImageUrl(member.image, { width: 600, height: 750, fit: 'cover' })} 
                    alt={member.name} 
                    className="w-full h-full object-cover transition-all duration-700" 
                    loading="lazy"
                  />
                  <div className="absolute inset-0 bg-[#090807]/5 group-hover:bg-transparent transition-colors duration-500" />
                </div>

                <div className="px-2">
                  <div className="flex items-start gap-3 mb-6">
                    <div className="h-px w-8 bg-[#CEBDAA] group-hover:w-12 group-hover:bg-[#A8814E] transition-all duration-500 mt-2.5 shrink-0" />
                    <div className="flex flex-col gap-1">
                      {member.role.split('\n').map((roleLine, idx) => (
                        <span key={idx} className="text-sm font-bold uppercase tracking-widest text-[#9A8E81] group-hover:text-[#1C1B1A] transition-colors block">
                          {roleLine}
                        </span>
                      ))}
                    </div>
                  </div>
                  
                  <h3 className="text-3xl lg:text-4xl font-black uppercase mb-2 leading-[0.95] text-[#1C1B1A]">
                    {member.name.split(' ').map((n, j) => (
                      <span key={j} className={j > 0 ? "text-stroke block mt-1" : "block"}>
                        {n}
                      </span>
                    ))}
                  </h3>

                  <p className="mt-4 text-sm text-[#494540] leading-relaxed font-medium">
                    {member.description}
                  </p>
                </div>
              </div>
            </motion.div>
          ))}
        </div>
      </Section>

      {/* Values / Studio Info */}
      <Section className="bg-white">
        <div className="max-w-6xl mx-auto flex flex-col md:flex-row items-center gap-20">
          <div className="md:w-1/2">
            <Heading subtitle="Studiet" className="mb-8 text-left">Træning i rolige <br/> omgivelser</Heading>
            <div className="space-y-8">
                  <div className="flex gap-6">
                    <div className="w-12 h-12 bg-[#F1E5DC] rounded-2xl flex items-center justify-center shrink-0">
                      <ShieldCheck className="w-6 h-6 text-[#A8814E]" />
                    </div>
                    <div>
                      <h4 className="text-lg font-black uppercase mb-2 text-[#1C1B1A]">Sikkerhed & Faglighed</h4>
                      <p className="text-[#494540] font-medium">Vi har en sundhedsfaglig baggrund og sikrer, at din træning er både sikker, behagelig og resultatorienteret.</p>
                    </div>
                  </div>
                  <div className="flex gap-6">
                    <div className="w-12 h-12 bg-[#F1E5DC] rounded-2xl flex items-center justify-center shrink-0">
                      <Users className="w-6 h-6 text-[#A8814E]" />
                    </div>
                    <div>
                      <h4 className="text-lg font-black uppercase mb-2 text-[#1C1B1A]">1:1 Personlig Træning</h4>
                      <p className="text-[#494540] font-medium">Hos LK STUDIO er der fuld fokus på dig. Vi guider dig i hver øvelse, så de bliver udført korrekt.</p>
                    </div>
                  </div>
                  <div className="flex gap-6">
                    <div className="w-12 h-12 bg-[#F1E5DC] rounded-2xl flex items-center justify-center shrink-0">
                      <Activity className="w-6 h-6 text-[#A8814E]" />
                    </div>
                    <div>
                      <h4 className="text-lg font-black uppercase mb-2 text-[#1C1B1A]">Hurtige Resultater</h4>
                      <p className="text-[#494540] font-medium">Vi udnytter teknologien to at give dig en effektiv helkropstræning på blot 20 minutter.</p>
                    </div>
                  </div>
                </div>
          </div>
          <div className="md:w-1/2 relative">
             <div className="aspect-square rounded-[4rem] overflow-hidden shadow-2xl rotate-3">
                <img 
                  src={getOptimizedImageUrl("https://assets.auxodev.dk/assets/766f1303-2002-4537-8cbb-d8bd7f1c5c0f/acf24aad-5bd9-4c13-ac2b-a8f96ec93620.jpg", { width: 800, quality: 80 })} 
                  alt="Studio" 
                  className="w-full h-full object-cover" 
                  loading="lazy"
                />
             </div>
             <motion.div 
               animate={{ rotate: [0, 360] }}
               transition={{ duration: 20, repeat: Infinity, ease: "linear" }}
               whileHover={{ scale: 1.1, transition: { duration: 0.3 } }}
               className="absolute -bottom-4 -left-4 md:-bottom-6 md:-left-6 lg:-bottom-10 lg:-left-10 w-32 h-32 md:w-40 md:h-40 lg:w-48 lg:h-48 bg-[#090807] rounded-full flex flex-col items-center justify-center text-white border-4 md:border-8 border-white shadow-2xl z-20 cursor-default"
             >
                <motion.div
                  animate={{ scale: [1, 1.2, 1] }}
                  transition={{ duration: 2, repeat: Infinity, ease: "easeInOut" }}
                >
                  <Heart className="w-5 h-5 md:w-8 md:h-8 mb-1 md:mb-2 fill-red-500 text-red-500" />
                </motion.div>
                <p className="text-center font-bold text-[10px] md:text-xs leading-tight uppercase tracking-widest">Vi elsker <br/> bevægelse</p>
             </motion.div>
          </div>
        </div>
      </Section>

      {/* Quote */}
      <Section className="pb-8 md:pb-20 lg:pb-32">
        <div className="max-w-4xl mx-auto text-center">
          <div className="text-9xl font-black text-[#CEBDAA]/20 select-none">"</div>
          <p className="text-2xl md:text-4xl font-black tracking-tight text-[#1C1B1A] -mt-16 relative z-10 leading-tight">
            Vi ser kroppen som en helhed og står altid klar til at hjælpe og behandle den aktuelle problemstilling.
          </p>
        </div>
      </Section>
    </div>
  );
};

export default About;
